import React from "react";
import PaginationMui from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { useResponsive } from "../../hooks/mediaQuery";
import { useTranslation } from "react-i18next";

type PaginationProps = {
    pageCount?: number;
    totalItemCount?: number;
    pageIndex?: number;
    pageSize?: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
};

const Pagination: React.FC<PaginationProps> = ({
    pageCount = 0,
    totalItemCount = 0,
    pageIndex = 0,
    pageSize = 0,
    onPageChange,
    onPageSizeChange,
}) => {
    const { isXs } = useResponsive();
    const { t } = useTranslation();
    // MUI Pagination uses 1-based page index
    const handleChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        if (value !== pageIndex) {
            onPageChange(value);
        }
    };

    return (
        <div className={`flex flex-wrap items-center justify-between ${isXs ? 'gap-2' : 'gap-4'} rounded-lg border border-gray-200 shadow-sm bg-white py-2 px-3`}>
            <div className="text-sm text-gray-600">
                {`${isXs ? '' : t('common.showPage')} ${(pageIndex - 1) * pageSize + 1} - ${Math.min(
                    pageIndex * pageSize,
                    totalItemCount
                )} / ${totalItemCount}`}
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {!isXs && (
                    <>
                        <div className="text-sm font-medium text-gray-600">{t('common.pageSize')}</div>
                        <FormControl size="small" variant="outlined">
                            <Select
                                value={pageSize}
                                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                                style={{ minWidth: 120 }}
                            >
                                {[10, 20, 50, 100].map((size) => (
                                    <MenuItem key={size} value={size}>{size}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                )}
                <PaginationMui
                    count={pageCount}
                    page={pageIndex}
                    onChange={handleChange}
                    color="primary"
                    shape="rounded"
                    showFirstButton={!isXs}
                    showLastButton={!isXs}
                    size={isXs ? 'small' : 'medium'}
                />
            </div>
        </div>
    );
};

export default Pagination;
